// Info Upload Page - Multi-input form for uploading files, text, and voice

import { useState, useCallback, useRef } from 'react';
import { Card, CardBody, CardHeader, Button } from '../components/common';
import { VoiceRecorder, InfoReviewCard } from '../components/info';
import { infoService } from '../services/info';
import { formatFileSize } from '../utils/dateFormatters';
import type {
  UploadPhase,
  ExtractedInfo,
  ItemApprovalStatus,
} from '../types/info';
import styles from './InfoUploadPage.module.css';

interface FileItem {
  file: File;
  id: string;
}

interface Recording {
  id: string;
  blob: Blob;
  duration: number;
  url: string;
}

export function InfoUploadPage() {
  // Phase state
  const [phase, setPhase] = useState<UploadPhase>('input');

  // Input state
  const [files, setFiles] = useState<FileItem[]>([]);
  const [textContent, setTextContent] = useState('');
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Submission state
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [extractedItems, setExtractedItems] = useState<ExtractedInfo[]>([]);
  const [overallContext, setOverallContext] = useState<string | null>(null);
  const [itemApprovals, setItemApprovals] = useState<Record<number, ItemApprovalStatus>>({});
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [refinementCount, setRefinementCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File handling
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const newFiles = selectedFiles.map((file) => ({
      file,
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const newFiles = droppedFiles.map((file) => ({
      file,
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  // Get file type badge
  const getFileTypeBadge = (filename: string): { label: string; color: string } => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const types: Record<string, { label: string; color: string }> = {
      pdf: { label: 'PDF', color: '#EE3C33' },
      doc: { label: 'DOC', color: '#0B4FA3' },
      docx: { label: 'DOC', color: '#0B4FA3' },
      txt: { label: 'TXT', color: '#2ecc71' },
      png: { label: 'IMG', color: '#f39c12' },
      jpg: { label: 'IMG', color: '#f39c12' },
      jpeg: { label: 'IMG', color: '#f39c12' },
    };
    return types[ext] || { label: ext.toUpperCase(), color: '#9b59b6' };
  };

  // Process content
  const handleProcess = async () => {
    if (files.length === 0 && !textContent.trim() && recordings.length === 0) {
      setError('Please add some content to process');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setPhase('processing');

    try {
      // Convert recordings to files
      const audioFiles = recordings.map((rec, i) => {
        const file = new File([rec.blob], `recording-${i + 1}.webm`, {
          type: rec.blob.type,
        });
        return file;
      });

      // Upload content
      const uploadResponse = await infoService.upload(
        files.map((f) => f.file),
        textContent || null,
        audioFiles
      );

      setSubmissionId(uploadResponse.submission_id);

      // Run extraction
      const extractionResponse = await infoService.extract(uploadResponse.submission_id);

      setExtractedItems(extractionResponse.extracted_items);
      setOverallContext(extractionResponse.overall_context);
      setRefinementCount(extractionResponse.refinement_count);

      // Initialize approval statuses
      const initialApprovals: Record<number, ItemApprovalStatus> = {};
      extractionResponse.extracted_items.forEach((_, i) => {
        initialApprovals[i] = 'pending';
      });
      setItemApprovals(initialApprovals);

      setPhase('review');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process content';
      setError(errorMessage);
      setPhase('input');
    } finally {
      setIsProcessing(false);
    }
  };

  // Approve item
  const handleApproveItem = async (index: number) => {
    if (!submissionId) return;

    setIsProcessing(true);
    try {
      await infoService.approve(submissionId, { item_indices: [index] });
      setItemApprovals((prev) => ({ ...prev, [index]: 'approved' }));

      // Check if all items are approved
      const allApproved = Object.values({ ...itemApprovals, [index]: 'approved' }).every(
        (s) => s === 'approved'
      );

      if (allApproved) {
        setPhase('approved');
      } else {
        // Move to next pending item
        const nextPending = extractedItems.findIndex(
          (_, i) => i !== index && itemApprovals[i] !== 'approved'
        );
        if (nextPending !== -1) {
          setCurrentItemIndex(nextPending);
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve item';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Provide feedback
  const handleFeedback = async (index: number, feedback: string) => {
    if (!submissionId) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await infoService.refine(submissionId, {
        feedback,
        feedback_source: 'text',
        item_index: index,
      });

      setExtractedItems(response.extracted_items);
      setOverallContext(response.overall_context);
      setRefinementCount(response.refinement_count);

      // Reset approval statuses
      const newApprovals: Record<number, ItemApprovalStatus> = {};
      response.extracted_items.forEach((_, i) => {
        newApprovals[i] = 'pending';
      });
      setItemApprovals(newApprovals);
      setCurrentItemIndex(0);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refine extraction';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Approve all
  const handleApproveAll = async () => {
    if (!submissionId) return;

    setIsProcessing(true);
    try {
      await infoService.approve(submissionId);
      const allApproved: Record<number, ItemApprovalStatus> = {};
      extractedItems.forEach((_, i) => {
        allApproved[i] = 'approved';
      });
      setItemApprovals(allApproved);
      setPhase('approved');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve all';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Start over
  const handleStartOver = () => {
    setPhase('input');
    setFiles([]);
    setTextContent('');
    setRecordings([]);
    setSubmissionId(null);
    setExtractedItems([]);
    setOverallContext(null);
    setItemApprovals({});
    setCurrentItemIndex(0);
    setRefinementCount(0);
    setError(null);
  };

  // Calculate summary
  const hasContent = files.length > 0 || textContent.trim() || recordings.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Upload Information</h1>
        <p>Add files, text, and voice recordings - submit everything together</p>
      </div>

      {/* Phase Indicator */}
      <div className={styles.phaseIndicator}>
        <span className={`${styles.phase} ${phase === 'input' ? styles.active : ''} ${phase !== 'input' ? styles.completed : ''}`}>
          1. Input
        </span>
        <span className={`${styles.phase} ${phase === 'processing' ? styles.active : ''} ${['review', 'approved'].includes(phase) ? styles.completed : ''}`}>
          2. Processing
        </span>
        <span className={`${styles.phase} ${phase === 'review' ? styles.active : ''} ${phase === 'approved' ? styles.completed : ''}`}>
          3. Review
        </span>
        <span className={`${styles.phase} ${phase === 'approved' ? styles.active : ''}`}>
          4. Approved
        </span>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* INPUT PHASE */}
      {phase === 'input' && (
        <div className={styles.inputGrid}>
          {/* Files Section */}
          <Card>
            <CardHeader
              action={
                <span className={styles.badge}>
                  {files.length} {files.length === 1 ? 'file' : 'files'}
                </span>
              }
            >
              <div className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📄</span>
                Files
              </div>
            </CardHeader>
            <CardBody>
              <div className={styles.uploadInstructions}>
                Upload trip reports, product specs, customer notes, or any documents for AI processing.
              </div>
              <div
                className={`${styles.uploadZone} ${files.length > 0 ? styles.hasFiles : ''} ${isDragging ? styles.dragging : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className={styles.uploadIcon}>{isDragging ? '\u2193' : '+'}</div>
                <div className={styles.uploadText}>
                  {isDragging ? 'Drop files here' : files.length > 0 ? 'Click to add more files' : 'Drop files here or click to upload'}
                </div>
                <div className={styles.uploadHint}>PDF, DOC, DOCX, TXT, PNG, JPG</div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                hidden
              />

              {files.length > 0 && (
                <div className={styles.fileList}>
                  {files.map((item) => {
                    const badge = getFileTypeBadge(item.file.name);
                    return (
                      <div key={item.id} className={styles.fileItem}>
                        <div
                          className={styles.fileIcon}
                          style={{ background: badge.color }}
                        >
                          {badge.label}
                        </div>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileName}>{item.file.name}</div>
                          <div className={styles.fileSize}>
                            {formatFileSize(item.file.size)}
                          </div>
                        </div>
                        <button
                          className={styles.fileRemove}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(item.id);
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Text Section */}
          <Card>
            <CardHeader
              action={
                <span className={styles.badge}>
                  {textContent.length} chars
                </span>
              }
            >
              <div className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📝</span>
                Text Notes
              </div>
            </CardHeader>
            <CardBody>
              <textarea
                className={styles.textArea}
                placeholder="Add any additional notes, context, or text content here..."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
              />
              <div className={styles.charCount}>
                {textContent.length.toLocaleString()} / 10,000 characters
              </div>
            </CardBody>
          </Card>

          {/* Voice Section */}
          <Card>
            <CardHeader
              action={
                <span className={`${styles.badge} ${styles.badgePurple}`}>
                  {recordings.length} {recordings.length === 1 ? 'recording' : 'recordings'}
                </span>
              }
            >
              <div className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>🎤</span>
                Voice Recordings
              </div>
            </CardHeader>
            <CardBody>
              <VoiceRecorder
                recordings={recordings}
                onRecordingsChange={setRecordings}
              />
            </CardBody>
          </Card>
        </div>
      )}

      {/* PROCESSING PHASE */}
      {phase === 'processing' && (
        <div className={styles.processingContainer}>
          <div className={styles.spinner} />
          <p>Processing your content...</p>
          <p className={styles.processingHint}>
            This may take a minute for transcription and extraction.
          </p>
        </div>
      )}

      {/* REVIEW PHASE */}
      {phase === 'review' && extractedItems.length > 0 && (
        <div className={styles.reviewContainer}>
          {overallContext && (
            <div className={styles.contextBanner}>
              <strong>Context:</strong> {overallContext}
            </div>
          )}

          {refinementCount > 0 && (
            <div className={styles.refinementBanner}>
              Refinement #{refinementCount} - Updated based on your feedback
            </div>
          )}

          {/* Item Navigation */}
          {extractedItems.length > 1 && (
            <div className={styles.itemNav}>
              {extractedItems.map((_, i) => (
                <button
                  key={i}
                  className={`${styles.itemNavBtn} ${i === currentItemIndex ? styles.active : ''} ${itemApprovals[i] === 'approved' ? styles.approved : ''}`}
                  onClick={() => setCurrentItemIndex(i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}

          <InfoReviewCard
            item={extractedItems[currentItemIndex]}
            index={currentItemIndex}
            totalItems={extractedItems.length}
            approvalStatus={itemApprovals[currentItemIndex]}
            onApprove={() => handleApproveItem(currentItemIndex)}
            onFeedback={(feedback) => handleFeedback(currentItemIndex, feedback)}
            isProcessing={isProcessing}
          />

          <div className={styles.reviewActions}>
            <Button variant="secondary" onClick={handleStartOver} disabled={isProcessing}>
              Start Over
            </Button>
            <Button onClick={handleApproveAll} disabled={isProcessing}>
              Approve All ({extractedItems.length})
            </Button>
          </div>
        </div>
      )}

      {/* APPROVED PHASE */}
      {phase === 'approved' && (
        <div className={styles.approvedContainer}>
          <div className={styles.approvedIcon}>✓</div>
          <h2>All Items Approved!</h2>
          <p>
            {extractedItems.length} {extractedItems.length === 1 ? 'item has' : 'items have'} been saved.
          </p>
          <Button onClick={handleStartOver}>Upload More</Button>
        </div>
      )}

      {/* Summary Bar (Input Phase) */}
      {phase === 'input' && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItems}>
            <div className={styles.summaryItem}>
              <div className={`${styles.summaryIcon} ${styles.iconFiles}`}>📄</div>
              <div>
                <div className={styles.summaryCount}>{files.length}</div>
                <div className={styles.summaryLabel}>Files</div>
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={`${styles.summaryIcon} ${styles.iconText}`}>📝</div>
              <div>
                <div className={styles.summaryCount}>{textContent.length}</div>
                <div className={styles.summaryLabel}>Characters</div>
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={`${styles.summaryIcon} ${styles.iconVoice}`}>🎤</div>
              <div>
                <div className={styles.summaryCount}>{recordings.length}</div>
                <div className={styles.summaryLabel}>Recordings</div>
              </div>
            </div>
          </div>
          <Button
            onClick={handleProcess}
            disabled={!hasContent || isProcessing}
          >
            Process All Content →
          </Button>
        </div>
      )}
    </div>
  );
}

export default InfoUploadPage;
